import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { AjustesTenant, Id, Maquina, Material, Parametro, SolicitudCotizacion } from '@laser/core';
import { api } from './cliente';
import type { AgruparReporte, CambiosAjustes, CambiosTrabajo, FiltrosApiTrabajos } from './tipos';

export const claves = {
  yo: ['yo'] as const,
  trabajos: (f?: FiltrosApiTrabajos) => (f ? (['trabajos', f] as const) : (['trabajos'] as const)),
  trabajo: (id: Id) => ['trabajo', id] as const,
  tiempo: (id: Id) => ['trabajo', id, 'tiempo'] as const,
  eventos: (id: Id) => ['trabajo', id, 'eventos'] as const,
  maquinas: ['maquinas'] as const,
  materiales: ['materiales'] as const,
  parametros: ['parametros'] as const,
  ajustes: ['ajustes'] as const,
  clientes: ['clientes'] as const,
  cliente: (id: Id) => ['clientes', id] as const,
  programa: ['programa'] as const,
  reportes: ['reportes'] as const,
  precision: ['precision'] as const,
};

export const useYo = () => useQuery({ queryKey: claves.yo, queryFn: api.yo, staleTime: 5 * 60_000 });

export function useTrabajos(f: FiltrosApiTrabajos) {
  return useInfiniteQuery({
    queryKey: claves.trabajos(f),
    queryFn: ({ pageParam }) => api.trabajos.listar({ ...f, limite: 100, cursor: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (ultima) => ultima.siguienteCursor,
    refetchInterval: 60_000,
  });
}

export const useTrabajo = (id: Id) => useQuery({ queryKey: claves.trabajo(id), queryFn: () => api.trabajos.obtener(id) });
export const useRegistros = (id: Id) => useQuery({ queryKey: claves.tiempo(id), queryFn: () => api.trabajos.tiempo(id) });
export const useEventos = (id: Id) => useQuery({ queryKey: claves.eventos(id), queryFn: () => api.trabajos.eventos(id) });

export const useMaquinas = () => useQuery({ queryKey: claves.maquinas, queryFn: api.maquinas.listar, staleTime: 60_000 });
export const useMateriales = () => useQuery({ queryKey: claves.materiales, queryFn: api.materiales.listar, staleTime: 60_000 });
export const useParametros = () => useQuery({ queryKey: claves.parametros, queryFn: api.parametros.listar, staleTime: 60_000 });
export const useAjustes = () => useQuery({ queryKey: claves.ajustes, queryFn: api.ajustes.obtener });
export const useClientes = () => useQuery({ queryKey: claves.clientes, queryFn: () => api.clientes.listar(), staleTime: 30_000 });
export const useCliente = (id: Id) => useQuery({ queryKey: claves.cliente(id), queryFn: () => api.clientes.obtener(id) });
export const usePrograma = () => useQuery({ queryKey: claves.programa, queryFn: api.programa, refetchInterval: 60_000 });
export const useReporte = (desde: string, hasta: string, agrupar: AgruparReporte) =>
  useQuery({ queryKey: [...claves.reportes, desde, hasta, agrupar], queryFn: () => api.reportes(desde, hasta, agrupar), enabled: Boolean(desde && hasta) });
export const usePrecision = (n = 20) => useQuery({ queryKey: [...claves.precision, n], queryFn: () => api.precision(n) });

/** Mapas id → entidad para cruzar en tablas. */
export function useCatalogo() {
  const maquinas = useMaquinas();
  const materiales = useMateriales();
  const clientes = useClientes();
  return useMemo(() => ({
    maquinas: maquinas.data ?? [],
    materiales: materiales.data ?? [],
    clientes: clientes.data ?? [],
    maquina: new Map((maquinas.data ?? []).map((m) => [m.id, m])),
    material: new Map((materiales.data ?? []).map((m) => [m.id, m])),
    cliente: new Map((clientes.data ?? []).map((c) => [c.id, c])),
    cargando: maquinas.isLoading || materiales.isLoading,
  }), [maquinas.data, materiales.data, clientes.data, maquinas.isLoading, materiales.isLoading]);
}

// ───────────── Mutaciones ─────────────

function useInvalidarTrabajo() {
  const qc = useQueryClient();
  return (id: Id) => Promise.all([
    qc.invalidateQueries({ queryKey: ['trabajo', id] }),
    qc.invalidateQueries({ queryKey: claves.trabajos() }),
    qc.invalidateQueries({ queryKey: claves.programa }),
    qc.invalidateQueries({ queryKey: claves.clientes }),
    qc.invalidateQueries({ queryKey: claves.reportes }),
  ]);
}

export function useActualizarTrabajo(id: Id) {
  const invalidar = useInvalidarTrabajo();
  return useMutation({ mutationFn: (c: CambiosTrabajo) => api.trabajos.actualizar(id, c), onSuccess: () => invalidar(id) });
}

export function useRecotizar(id: Id) {
  const invalidar = useInvalidarTrabajo();
  return useMutation({ mutationFn: (s: SolicitudCotizacion) => api.trabajos.recotizar(id, s), onSuccess: () => invalidar(id) });
}

export function useAprobar(id: Id) {
  const invalidar = useInvalidarTrabajo();
  return useMutation({ mutationFn: () => api.trabajos.aprobar(id), onSuccess: () => invalidar(id) });
}

export function useTemporizador(id: Id) {
  const invalidar = useInvalidarTrabajo();
  const iniciar = useMutation({ mutationFn: (d: { maquinaId: Id; nota?: string }) => api.trabajos.iniciarTiempo(id, d), onSuccess: () => invalidar(id) });
  const detener = useMutation({ mutationFn: (d: { maquinaId: Id; nota?: string }) => api.trabajos.detenerTiempo(id, d), onSuccess: () => invalidar(id) });
  return { iniciar, detener };
}

export function useGuardarMaquina() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id?: Id; datos: Omit<Maquina, 'id' | 'tenantId'> }) =>
      id ? api.maquinas.actualizar(id, datos) : api.maquinas.crear(datos),
    onSuccess: () => { qc.invalidateQueries({ queryKey: claves.maquinas }); qc.invalidateQueries({ queryKey: claves.programa }); },
  });
}

export function useEliminarMaquina() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: Id) => api.maquinas.eliminar(id), onSuccess: () => { qc.invalidateQueries({ queryKey: claves.maquinas }); qc.invalidateQueries({ queryKey: claves.parametros }); } });
}

export function useGuardarMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id?: Id; datos: Omit<Material, 'id' | 'tenantId'> }) =>
      id ? api.materiales.actualizar(id, datos) : api.materiales.crear(datos),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.materiales }),
  });
}

export function useEliminarMaterial() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: Id) => api.materiales.eliminar(id), onSuccess: () => { qc.invalidateQueries({ queryKey: claves.materiales }); qc.invalidateQueries({ queryKey: claves.parametros }); } });
}

export function useGuardarParametro() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (p: Parametro) => api.parametros.guardar(p), onSuccess: () => qc.invalidateQueries({ queryKey: claves.parametros }) });
}

export function useEliminarParametro() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (p: Pick<Parametro, 'maquinaId' | 'materialId'>) => api.parametros.eliminar(p.maquinaId, p.materialId), onSuccess: () => qc.invalidateQueries({ queryKey: claves.parametros }) });
}

export function useGuardarAjustes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (c: CambiosAjustes) => api.ajustes.actualizar(c),
    onSuccess: (a: AjustesTenant) => qc.setQueryData(claves.ajustes, a),
  });
}
